import FileInputV3 from '@efex-components/EfexDS/FileInput/FileInput';
import { StakeHoldersFormData } from '@efex-services/client/types';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import IdRequirementsAlert from '@pages/onboarding/components/IdRequirementsAlert/IdRequirementsAlert';
import { USA_COMPANY_DOCUMENTS } from '@pages/onboarding/USA/constants/config';
import { DateTime } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEffectOnce from '@main-hooks/useEffectOnce';
import styles from './styles';
import StakeHolderContext from '../../../context/StakeHolderContext';
import GovermentIdSelector from './GovermentIdSelector';

export interface DocumentTypeProps {
  handleDropChangeFile: (files: FileList, name: string) => void;
  defaultValues: Omit<
    StakeHoldersFormData,
    'neighborhood' | 'secondLastName' | 'kycStatus'
  > | null;
}

const DocumentType = ({
  handleDropChangeFile,
  defaultValues,
}: DocumentTypeProps) => {
  const [selectedCompanyDocument, setSelectedCompanyDocument] = useState('');

  const {
    govermentIdDocuments,
    setGovermentIdDocuments,
    changeProofOfAddressDocument,
    deleteGovernmentIdDocument,
  } = useContext(StakeHolderContext);

  const { t } = useTranslation();

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  useEffectOnce(() => {
    setGovermentIdDocuments(defaultValues?.documents || []);
  });

  useEffect(() => {
    if (
      !govermentIdDocuments.documentOne &&
      !govermentIdDocuments.documentTwo
    ) {
      setValue('effectiveAt', '');
      setValue('expiredAt', '');
      setValue('idNumber', '');
    } else {
      setValue('effectiveAt', defaultValues?.effectiveAt);
      setValue('expiredAt', defaultValues?.expiredAt);
      setValue('idNumber', defaultValues?.idNumber);
    }

    if (!selectedCompanyDocument) return;

    setValue(selectedCompanyDocument, govermentIdDocuments.documentOne);

    if (['driversLicence', 'personalId'].includes(selectedCompanyDocument)) {
      setValue(
        `${selectedCompanyDocument}Back`,
        govermentIdDocuments.documentTwo || ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyDocument, govermentIdDocuments]);

  return (
    <Box sx={{ marginTop: 2 }}>
      <Typography variant="subtitle1" className="!ml-2">
        {t('onbRegistrycompanyDocumentType')}
      </Typography>

      <IdRequirementsAlert />

      <Grid sx={{ marginTop: 2 }} item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="idType">
            {t('onbRegistrycompanyDocumentType')}
          </InputLabel>

          <Controller
            name="idType"
            rules={{ required: true }}
            control={control}
            render={({ field: { onChange, value } }) => (
              <GovermentIdSelector
                value={value}
                onChange={onChange}
                errors={errors}
                setSelectedCompanyDocument={setSelectedCompanyDocument}
              />
            )}
          />
        </FormControl>

        {errors && errors.idType && errors.idType.type === 'required' && (
          <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
        )}
      </Grid>

      {selectedCompanyDocument && (
        <>
          <div className="w-full px-3">
            <Controller
              name={selectedCompanyDocument}
              rules={{ required: true }}
              control={control}
              render={() => (
                <FileInputV3
                  isSingleFile
                  key={selectedCompanyDocument}
                  inputName={selectedCompanyDocument}
                  onChange={changeProofOfAddressDocument}
                  onDropChange={handleDropChangeFile}
                  onDelete={() => deleteGovernmentIdDocument('first')}
                  documents={
                    govermentIdDocuments.documentOne
                      ? [govermentIdDocuments.documentOne]
                      : []
                  }
                  title={`${t(
                    USA_COMPANY_DOCUMENTS.find(
                      (doc) => doc.name === selectedCompanyDocument
                    )?.label ?? ''
                  )} ${t('usaFrontDocument')}`}
                  wrapperClasses="!m-0 !mb-2"
                  error={
                    errors[selectedCompanyDocument]
                      ? new Error(t('onbDocumentRequired') as string)
                      : undefined
                  }
                />
              )}
            />
          </div>

          {(selectedCompanyDocument === 'driversLicence' ||
            selectedCompanyDocument === 'personalId') && (
            <div className="w-full px-3">
              <Controller
                name={`${selectedCompanyDocument}Back`}
                rules={{ required: true }}
                control={control}
                render={() => (
                  <FileInputV3
                    isSingleFile
                    key={`${selectedCompanyDocument}Back`}
                    inputName={`${selectedCompanyDocument}Back`}
                    onChange={changeProofOfAddressDocument}
                    onDropChange={handleDropChangeFile}
                    onDelete={() => deleteGovernmentIdDocument('second')}
                    documents={
                      govermentIdDocuments.documentTwo
                        ? [govermentIdDocuments.documentTwo]
                        : []
                    }
                    title={
                      selectedCompanyDocument === 'driversLicence'
                        ? (t('usaDocDriversLicenceBack') as string)
                        : (t('usaDocPersonalIdBack') as string)
                    }
                    wrapperClasses="!m-0 !mb-2"
                    error={
                      errors[`${selectedCompanyDocument}Back`]
                        ? new Error(t('onbDocumentRequired') as string)
                        : undefined
                    }
                  />
                )}
              />
            </div>
          )}
        </>
      )}

      <Grid className="pt-2" container rowSpacing={2} columnSpacing={2}>
        {selectedCompanyDocument && selectedCompanyDocument !== '' ? (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t(`onbEffectiveDate`)}
                </Typography>
                <Controller
                  name="effectiveAt"
                  rules={{ required: true }}
                  control={control}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                      <DatePicker
                        {...field}
                        className={styles.dropdownStyles}
                        onChange={(value) => {
                          if (!value) return;
                          field.onChange(value);
                        }}
                        value={
                          field.value
                            ? DateTime.fromISO(field.value as string)
                            : null
                        }
                        disableFuture
                        slotProps={{
                          // eslint-disable-next-line react/no-unstable-nested-components
                          textField: (params) => (
                            <TextField
                              {...params}
                              size="small"
                              error={!!errors.effectiveAt}
                              fullWidth
                            />
                          ),
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
                {errors && errors.effectiveAt && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t(`onbExpiryDate`)}
                </Typography>
                <Controller
                  name="expiredAt"
                  rules={{ required: true }}
                  control={control}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                      <DatePicker
                        {...field}
                        className={styles.dropdownStyles}
                        onChange={(value) => {
                          if (!value) return;
                          field.onChange(value);
                        }}
                        value={
                          field.value
                            ? DateTime.fromISO(field.value as string)
                            : null
                        }
                        disablePast
                        slotProps={{
                          // eslint-disable-next-line react/no-unstable-nested-components
                          textField: (params) => (
                            <TextField
                              {...params}
                              size="small"
                              error={!!errors.expiredAt}
                              fullWidth
                            />
                          ),
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
                {errors && errors.expiredAt && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2">{t(`onbNumber`)}</Typography>
                <TextField
                  autoComplete="off"
                  type="text"
                  className="custom-textfield w-[90%]"
                  size="small"
                  error={!!errors.idNumber}
                  placeholder={t(`onbNumber`) as string}
                  {...register('idNumber', { required: true })}
                />
                {errors && errors.idNumber && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
          </>
        ) : null}
      </Grid>
    </Box>
  );
};

export default DocumentType;
